import * as React from "react";
import { makeStyles, shorthands, Spinner, mergeClasses } from "@fluentui/react-components";
import {
    Dismiss24Regular,
    Add20Regular,
    ChevronDown20Regular,
    ChevronRight20Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router";
import { vorgangService, VorgangDto, TodoDto } from "../services/vorgangService";
import { useUserLabels } from "../hooks/useUserLabels";
import { REFINED } from "./risikoanalyseStep/tokens";
import { betreffOhneArt, zeigtArtChip } from "../data/vorgangArt";
import { StatusCell } from "./vorgang/shared/StatusCell";
import { deriveVorgangStatus } from "./vorgang/shared/vorgangStatus";
import { PeopleCell } from "./vorgang/shared/PeopleCell";
import { beteiligteGruppen } from "./vorgang/shared/unifiedRows";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import "./redesign/contactRedesign.css";
import { openVorgangCreateFlow } from "./vorgang/create/VorgangCreateFlowWindow";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contactId: number | null;
    contactName: string;
}

// Spalten: ⌄ · Status · Betreff(+Beschreibung) · Beteiligte · Frist · TWV
//
// Keine Typ-Spalte mehr (PO 08.09.2026: „Unterscheidung Vorgang/To-do gibt es
// nicht mehr, nur noch Vorgang"): die eingerückten Zeilen unter einem Vorgang
// sind seine Schritte, das sagt der Aufklapp-Pfeil deutlicher als ein Badge.
const GRID_COLS = "28px 140px minmax(220px, 1fr) 132px 92px 92px";

const useStyles = makeStyles({
    backdrop: {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(36,21,34,0.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        ...shorthands.padding("24px"),
    },
    panel: {
        backgroundColor: REFINED.white,
        color: REFINED.ink,
        fontFamily: REFINED.fontUI,
        borderRadius: "16px",
        boxShadow: REFINED.cardShadow,
        width: "min(980px, 100%)",
        maxHeight: "84vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        ...shorthands.gap("12px"),
        ...shorthands.padding("18px", "22px", "12px", "22px"),
    },
    title: { margin: 0, fontSize: "1.25rem", fontWeight: 700, color: REFINED.ink },
    closeBtn: {
        appearance: "none",
        ...shorthands.border("none"),
        backgroundColor: "transparent",
        cursor: "pointer",
        color: REFINED.ink2,
        display: "inline-flex",
        ...shorthands.padding("4px"),
        borderRadius: "8px",
        ":hover": { backgroundColor: REFINED.aub50, color: REFINED.aub },
    },
    actions: {
        display: "flex",
        ...shorthands.gap("8px"),
        ...shorthands.padding("0", "22px", "12px", "22px"),
        ...shorthands.borderBottom("1px", "solid", REFINED.line),
    },
    btnPrimary: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        ...shorthands.padding("8px", "16px"),
        borderRadius: "10px",
        ...shorthands.borderWidth("0"),
        backgroundColor: REFINED.aub,
        color: REFINED.white,
        fontFamily: REFINED.fontUI,
        fontSize: "0.88rem",
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: REFINED.btnShadow,
        ":hover": { backgroundColor: REFINED.aub700 },
    },
    btnGhost: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        ...shorthands.padding("8px", "16px"),
        borderRadius: "10px",
        ...shorthands.border("1.5px", "solid", REFINED.aub),
        backgroundColor: REFINED.white,
        color: REFINED.aub,
        fontFamily: REFINED.fontUI,
        fontSize: "0.88rem",
        fontWeight: 600,
        cursor: "pointer",
        ":hover": { backgroundColor: REFINED.aub50 },
    },
    body: { overflowY: "auto", ...shorthands.padding("4px", "0", "8px", "0") },
    headRow: {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        alignItems: "center",
        ...shorthands.gap("10px"),
        ...shorthands.padding("8px", "22px"),
        position: "sticky",
        top: 0,
        backgroundColor: REFINED.white,
        ...shorthands.borderBottom("1px", "solid", REFINED.line),
        fontSize: "0.72rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: REFINED.ink3,
        zIndex: 1,
    },
    row: {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        alignItems: "center",
        ...shorthands.gap("10px"),
        ...shorthands.padding("10px", "22px"),
        ...shorthands.borderBottom("1px", "solid", REFINED.line),
        cursor: "pointer",
        ":hover": { backgroundColor: REFINED.aub50 },
    },
    childRow: { backgroundColor: "#fbfaff" },
    chevCell: { display: "inline-flex", alignItems: "center", justifyContent: "center" },
    artChip: {
        display: "inline-block",
        marginRight: "6px",
        ...shorthands.padding("1px", "7px"),
        borderRadius: "999px",
        backgroundColor: REFINED.aub50,
        color: REFINED.aub,
        fontSize: "0.72rem",
        fontWeight: 800,
        letterSpacing: "0.04em",
        verticalAlign: "1px",
    },
    chevron: {
        display: "inline-flex",
        cursor: "pointer",
        color: REFINED.ink3,
        ":hover": { color: REFINED.aub },
    },
    chevronSpacer: { width: "20px", display: "inline-block" },
    titelWrap: { minWidth: 0 },
    titelWrapChild: { ...shorthands.padding("0", "0", "0", "14px") },
    titelText: {
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: REFINED.ink,
    },
    titelTextChild: { fontWeight: 400, color: REFINED.ink },
    descText: {
        fontSize: "0.8rem",
        color: REFINED.ink3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    date: { whiteSpace: "nowrap", fontSize: "0.88rem", color: REFINED.ink2 },
    muted: { color: REFINED.ink3 },
    center: { display: "flex", justifyContent: "center", ...shorthands.padding("40px") },
    error: { ...shorthands.padding("24px"), textAlign: "center", color: REFINED.err },
    empty: {
        ...shorthands.padding("36px"),
        textAlign: "center",
        color: REFINED.ink3,
        fontStyle: "italic",
    },
});

const fmtDate = (d?: string | null): string =>
    d ? new Date(d).toLocaleDateString("de-DE") : "—";

const isOpenTodo = (t: TodoDto) => (t.status ?? "") !== "Erledigt";

/**
 * Übersicht der offenen Vorgänge eines Kontakts, aufgerufen aus der
 * Kontakttabelle. Eigenes Popup + eigene CSS-Grid-Tabelle (statt
 * Fluent-Dialog/Table — verhindert überlappende Header). Ein Vorgang ist der
 * Gruppenkopf seiner ein-/ausklappbaren Schritte; Klick auf eine Zeile öffnet
 * die Detailseite. Termine/Schäden folgen, sobald die Quellen final sind.
 *
 * PO 08.09.2026: keine Typ-Spalte („Vorgang/To-do" ist keine Unterscheidung
 * mehr), „Titel" heißt „Betreff", und statt der Bearbeiter stehen die
 * BETEILIGTEN da — als Kreise mit Profilfoto bzw. Kürzel, wie in der
 * Vorgangstabelle.
 */
export const OffeneVorgaengeDialog: React.FC<Props> = ({
    open,
    onOpenChange,
    contactId,
    contactName,
}) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { users } = useUserLabels();
    const { openWindow } = useWindowManager();
    const [vorgaenge, setVorgaenge] = React.useState<VorgangDto[]>([]);
    const [standaloneTodos, setStandaloneTodos] = React.useState<TodoDto[]>([]);
    const [collapsed, setCollapsed] = React.useState<Set<number>>(new Set());
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        if (!contactId) return;
        setLoading(true);
        setError(null);
        try {
            const [allVorgaenge, linkedTodos] = await Promise.all([
                vorgangService.getVorgaengeByContact(contactId, { force: true }),
                vorgangService.getTodosByLinked("KUNDE", contactId, { force: true }),
            ]);
            // "offen" wie überall (`deriveVorgangStatus`): ein von Hand
            // erledigter Vorgang ohne To-dos gehört nicht hierher (T-156) —
            // dieselbe Regel wie das „V" in der Kontaktliste.
            const openVorgaenge = allVorgaenge.filter((v) => deriveVorgangStatus(v) === "Offen");
            const nestedTodoIds = new Set<number>(
                openVorgaenge.flatMap((v) => (v.todos ?? []).filter(isOpenTodo).map((t) => t.id)),
            );
            const openStandalone = linkedTodos.filter(
                (t) => isOpenTodo(t) && !nestedTodoIds.has(t.id),
            );
            setVorgaenge(openVorgaenge);
            setStandaloneTodos(openStandalone);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Fehler beim Laden");
        } finally {
            setLoading(false);
        }
    }, [contactId]);

    React.useEffect(() => {
        if (!open || !contactId) return;
        let cancelled = false;
        (async () => {
            if (!cancelled) await load();
        })();
        return () => {
            cancelled = true;
        };
    }, [open, contactId, load]);

    const close = () => onOpenChange(false);

    const goto = (path: string) => {
        onOpenChange(false);
        navigate(path);
    };

    const toggle = (id: number) =>
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const openNeuerVorgang = () => {
        onOpenChange(false);
        // Create-Rework CR-SP7: EIN Einstieg — der zweistufige Flow (Zuweisung zuerst).
        openVorgangCreateFlow(openWindow, {
            initialContactId: contactId ?? null,
            initialContactName: contactName || null,
        });
    };

    const hasContent = vorgaenge.length > 0 || standaloneTodos.length > 0;

    const dateCells = (frist?: string | null, twv?: string | null) => (
        <>
            <span className={styles.date}>{fmtDate(frist)}</span>
            <span className={styles.date}>{fmtDate(twv)}</span>
        </>
    );

    /**
     * Betreff-Zelle: Art als Chip, daneben der Betreff — dieselbe Darstellung wie
     * im Vorgänge-Reiter der Kontaktakte. Bestands-Vorgänge tragen das Kürzel als
     * Titel; dort bleibt es beim Chip, statt „NKG NKG" zu schreiben.
     */
    const titelCell = (
        titel: string,
        beschreibung?: string | null,
        isChild?: boolean,
        art?: string | null,
    ) => (
        <div className={mergeClasses(styles.titelWrap, isChild && styles.titelWrapChild)}>
            <div className={mergeClasses(styles.titelText, isChild && styles.titelTextChild)} title={titel}>
                {zeigtArtChip(art) && <span className={styles.artChip}>{(art as string).trim()}</span>}
                {betreffOhneArt(art, titel)}
            </div>
            {beschreibung && (
                <div className={styles.descText} title={beschreibung}>
                    {beschreibung}
                </div>
            )}
        </div>
    );

    /**
     * To-do-Zeile. `vorgangId` = Eltern-Vorgang, falls bekannt.
     *
     * To-dos haben keine eigene Detailseite mehr — als Kindzeile eines geladenen
     * Vorgangs öffnet die Zeile deshalb DIESEN Vorgang (dort steckt die
     * To-do-Detailansicht in der Todos-Karte).
     *
     * Für die standalone-Zeilen unten wird bewusst KEIN Ziel übergeben: das sind
     * KUNDE-verlinkte To-dos, die unter keinem der geladenen Vorgänge hängen.
     * Elternlos sind sie nicht — ihr Vorgang gehört nur einem anderen Kunden
     * bzw. wurde hier nicht geladen. Ein Sprung dorthin würde aus dem Kontext
     * dieses Kontakts herausführen (fremde Kundenakte, ggf. 403/404), deshalb
     * bleibt die Zeile reine Anzeige.
     */
    const todoRow = (t: TodoDto, isChild: boolean, vorgangId?: number) => (
        <div
            key={`todo-${t.id}`}
            className={mergeClasses(styles.row, isChild && styles.childRow)}
            style={vorgangId == null ? { cursor: "default" } : undefined}
            onClick={vorgangId != null ? () => goto(`/vorgaenge/${vorgangId}`) : undefined}
            role={vorgangId != null ? "button" : undefined}
            tabIndex={vorgangId != null ? 0 : undefined}
        >
            <span className={styles.chevCell} />
            <StatusCell kind="todo" status={t.status} />
            {titelCell(t.titel, t.beschreibung, isChild)}
            <PeopleCell groups={beteiligteGruppen(users, t.hauptverantwortlich, t.bearbeiter, t.zuInformierende)} />
            {dateCells(t.frist, t.twv)}
        </div>
    );

    return (
        <>
            {open && (
                <div className={styles.backdrop} onClick={close}>
                    <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.header}>
                            <h2 className={styles.title}>
                                Offene Vorgänge{contactName ? `: ${contactName}` : ""}
                            </h2>
                            <button className={styles.closeBtn} aria-label="Schließen" onClick={close}>
                                <Dismiss24Regular />
                            </button>
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.btnPrimary} onClick={openNeuerVorgang} disabled={!contactId}>
                                <Add20Regular />
                                Neuer Vorgang
                            </button>
                        </div>

                        <div className={styles.body}>
                            {loading ? (
                                <div className={styles.center}>
                                    <Spinner label="Wird geladen…" />
                                </div>
                            ) : error ? (
                                <div className={styles.error}>{error}</div>
                            ) : !hasContent ? (
                                <div className={styles.empty}>Keine offenen Vorgänge vorhanden.</div>
                            ) : (
                                <>
                                    <div className={styles.headRow}>
                                        <span />
                                        <span>Status</span>
                                        <span>Betreff</span>
                                        <span>Beteiligte</span>
                                        <span>Frist</span>
                                        <span>TWV</span>
                                    </div>
                                    {vorgaenge.map((v) => {
                                        const childTodos = (v.todos ?? []).filter(isOpenTodo);
                                        const isOpen = !collapsed.has(v.id);
                                        return (
                                            <React.Fragment key={`vorgang-${v.id}`}>
                                                <div
                                                    className={styles.row}
                                                    onClick={() => goto(`/vorgaenge/${v.id}`)}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <span className={styles.chevCell}>
                                                        {childTodos.length > 0 ? (
                                                            <span
                                                                className={styles.chevron}
                                                                role="button"
                                                                aria-label={isOpen ? "Zuklappen" : "Aufklappen"}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggle(v.id);
                                                                }}
                                                            >
                                                                {isOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                                                            </span>
                                                        ) : (
                                                            <span className={styles.chevronSpacer} />
                                                        )}
                                                    </span>
                                                    <StatusCell
                                                        kind="vorgang"
                                                        status={v.status}
                                                        erledigt={v.fortschrittErledigt}
                                                        gesamt={v.fortschrittGesamt}
                                                    />
                                                    {titelCell(v.titel, v.beschreibung, false, v.vorgangsart)}
                                                    <PeopleCell
                                                        groups={beteiligteGruppen(
                                                            users,
                                                            v.hauptverantwortlich,
                                                            v.bearbeiter as unknown[],
                                                            v.zuInformierende,
                                                        )}
                                                    />
                                                    {dateCells(v.frist, v.twv)}
                                                </div>
                                                {isOpen && childTodos.map((t) => todoRow(t, true, v.id))}
                                            </React.Fragment>
                                        );
                                    })}
                                    {standaloneTodos.map((t) => todoRow(t, false))}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};
